// What a club calls its teams, and the colours a team can have.
//
// A club whose organisation has teams (a school) puts each athlete in one
// team: a house, a faction or a colour team. Each club chooses its own word
// in Club Settings, and the API sends that choice with the club as a number:
//
//   0  Team      the product's own word, and the usual one in American schools
//   1  House     the usual word in Australian schools
//   2  Faction   the word in Western Australian schools
//
// The database, the API and the code always say "team". Only text a user
// reads goes through this file, and nothing outside it should choose between
// Team, House and Faction itself, so that a word can be changed here once.
//
// This is the website's copy of the words in the API's
// Services/Calculation/TeamRules.cs, and teamLabel.test.js holds it to the
// same table of cases. Whether a club has teams at all is a different
// question: that is clubFeaturesOf(club).teams in utils/clubFeatures.js.

// The numbers are the API's TeamLabel.
export const TEAM_LABEL = { TEAM: 0, HOUSE: 1, FACTION: 2 };

// The label of a club object from the clubs list. A club with no value (an
// older API, or no club selected) reads Team.
export const teamLabelOf = (club) =>
  club?.teamLabel === TEAM_LABEL.HOUSE || club?.teamLabel === TEAM_LABEL.FACTION
    ? club.teamLabel
    : TEAM_LABEL.TEAM;

//   team, teams   one team and several, capitalised: a heading or a button
//   lower         the same words inside a sentence: "house", "factions"
// "A" goes before each of them ("a team", "a house", "a faction").
const wordsFor = (team, teams) => ({
  team,
  teams,
  lower: { team: team.toLowerCase(), teams: teams.toLowerCase() },
});

const WORDS = {
  [TEAM_LABEL.TEAM]: wordsFor('Team', 'Teams'),
  [TEAM_LABEL.HOUSE]: wordsFor('House', 'Houses'),
  [TEAM_LABEL.FACTION]: wordsFor('Faction', 'Factions'),
};

// The words for a label. A value that is not one of the three reads Team.
export const teamWords = (label) => WORDS[label] || WORDS[TEAM_LABEL.TEAM];

// The words the given club reads.
export const teamWordsOf = (club) => teamWords(teamLabelOf(club));

// The choices offered in Club Settings.
export const TEAM_LABEL_OPTIONS = [
  { value: TEAM_LABEL.TEAM, label: 'Teams' },
  { value: TEAM_LABEL.HOUSE, label: 'Houses' },
  { value: TEAM_LABEL.FACTION, label: 'Factions' },
];

// ---- Colours ----

// The colours a team can be given. A team's colour is optional. It is shown
// as a small dot beside the team's name, so the choice is a fixed palette of
// common house colours that all read clearly at that size, not a free colour
// picker. The server stores any "#RRGGBB" in upper case, so a colour picker
// could be added later without changing what is stored.
export const TEAM_COLOURS = [
  { name: 'Red', value: '#D32F2F' },
  { name: 'Maroon', value: '#7F1D1D' },
  { name: 'Orange', value: '#F57C00' },
  { name: 'Yellow', value: '#FDD835' },
  { name: 'Gold', value: '#C9A227' },
  { name: 'Green', value: '#2E7D32' },
  { name: 'Sky blue', value: '#4FC3F7' },
  { name: 'Blue', value: '#1565C0' },
  { name: 'Navy', value: '#1A237E' },
  { name: 'Purple', value: '#6A1B9A' },
  { name: 'Black', value: '#212121' },
  { name: 'White', value: '#FFFFFF' },
];

// The palette name of a stored colour ("Red"), or '' for no colour or one
// that is not in the palette.
export const teamColourName = (colour) =>
  TEAM_COLOURS.find(
    (entry) => entry.value === String(colour || '').toUpperCase()
  )?.name || '';

// ---- The team list ----

// The team with the given id in a club's team list, or null: for no id, and
// for the id of a team that has since been deleted.
export const teamById = (teams, teamId) =>
  (teamId && (teams || []).find((team) => team.teamID === teamId)) || null;

// The teams found on a list of rows that each carry their own team (a report
// row's `team`, as the server sends it: { teamID, name, colour, sortOrder }),
// each once, in the club's order: by sortOrder, then name, as the server lists
// a club's teams. A page that has its rows needs no request for the team list.
export const teamsOfRows = (rows, teamOf) => {
  const byId = new Map();
  (rows || []).forEach((row) => {
    const team = teamOf(row);
    if (team?.teamID && !byId.has(team.teamID)) byId.set(team.teamID, team);
  });
  return [...byId.values()].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || '').localeCompare(b.name || '')
  );
};

// ---- Filtering a list of athletes by team ----

// The filter value that stands for "in no team". A team's own value is its id.
export const NO_TEAM = 'none';

// The choices of a team filter: each of the club's teams, then "No house".
export const teamFilterOptions = (teams, words) => [
  ...(teams || []).map((team) => ({ value: team.teamID, label: team.name })),
  { value: NO_TEAM, label: `No ${words.lower.team}` },
];

// The ticked values that are still choices. A filter kept from an earlier
// visit can hold the id of a team that has since been deleted; it is dropped,
// so the list is not narrowed by a choice the user can no longer see.
export const activeTeamFilter = (ticked, options) => {
  const offered = new Set((options || []).map((option) => option.value));
  return (ticked || []).filter((value) => offered.has(value));
};

// Whether an athlete with this team id passes the filter. Nothing ticked
// passes everyone. An athlete whose team is no longer in the club's list
// counts as being in no team, which is how the list shows them.
export const matchesTeamFilter = (teamId, activeFilter, teams) => {
  if (!activeFilter || activeFilter.length === 0) return true;
  const team = teamById(teams, teamId);
  return activeFilter.includes(team ? team.teamID : NO_TEAM);
};
